/**
 * Origem Flow: captura de tela para os comentários. Um único quadro é tirado
 * pelo seletor do navegador (aba, janela ou monitor) e recortado no próprio
 * app, sem ferramenta do sistema nem de terceiros.
 */

// Quanto esperar pelo primeiro quadro do compartilhamento antes de desistir.
const FIRST_FRAME_TIMEOUT = 5000
// Folga para o seletor do navegador sumir da tela antes de tirar o quadro.
const SETTLE_DELAY = 300
// Arrastos menores que isso (em fração da imagem) contam como clique solto.
const MIN_SELECTION = 0.01

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

export const isScreenCaptureSupported = () =>
  typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia

/**
 * O usuário fechou o seletor sem escolher nada: não é erro para avisar. O
 * bloqueio pelo sistema operacional usa o mesmo nome de erro, mas precisa de
 * aviso — a mensagem cita "system" nesse caso.
 */
export const isScreenCaptureCancel = (error) =>
  ['NotAllowedError', 'AbortError'].includes(error?.name) &&
  !/system/i.test(error?.message || '')

async function waitForFirstFrame(video) {
  const deadline = Date.now() + FIRST_FRAME_TIMEOUT
  while (video.videoWidth === 0 || video.readyState < 2) {
    if (Date.now() > deadline) {
      throw new Error('No frame received from the screen capture.')
    }
    await wait(50)
  }
}

/**
 * Abre o seletor de tela do navegador e devolve um canvas com um quadro em
 * resolução nativa. O compartilhamento é encerrado antes de retornar.
 */
export async function captureScreenFrame() {
  const options = {
    video: true,
    audio: false,
    // A própria aba do Origem Flow também pode ser o alvo do print.
    selfBrowserSurface: 'include',
    surfaceSwitching: 'exclude',
  }
  if (typeof CaptureController !== 'undefined') {
    // Sem isso o Chrome leva o foco para a aba/janela capturada e o usuário
    // perde de vista a tela de recorte.
    const controller = new CaptureController()
    try {
      controller.setFocusBehavior('no-focus-change')
      options.controller = controller
    } catch (error) {
      // Navegador sem controle de foco: segue com o comportamento padrão.
    }
  }
  const stream = await navigator.mediaDevices.getDisplayMedia(options)
  try {
    const video = document.createElement('video')
    video.muted = true
    video.playsInline = true
    video.srcObject = stream
    await video.play()
    await waitForFirstFrame(video)
    await wait(SETTLE_DELAY)
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    canvas.getContext('2d').drawImage(video, 0, 0)
    video.srcObject = null
    return canvas
  } finally {
    stream.getTracks().forEach((track) => track.stop())
  }
}

const clamp = (value) => Math.min(1, Math.max(0, value))

/**
 * Seleção normalizada (frações 0–1 da imagem) a partir dos dois cantos do
 * arrasto, em qualquer direção. Devolve null para um clique sem arrasto.
 */
export function normalizeSelection(start, end) {
  const left = clamp(Math.min(start.x, end.x))
  const top = clamp(Math.min(start.y, end.y))
  const width = clamp(Math.max(start.x, end.x)) - left
  const height = clamp(Math.max(start.y, end.y)) - top
  if (width < MIN_SELECTION || height < MIN_SELECTION) {
    return null
  }
  return { left, top, width, height }
}

/**
 * Converte a seleção em frações para um retângulo em pixels da imagem de
 * origem; sem seleção, a imagem inteira.
 */
export function selectionToSourceRect(selection, sourceWidth, sourceHeight) {
  if (!selection) {
    return { x: 0, y: 0, width: sourceWidth, height: sourceHeight }
  }
  // O canto fica sempre dentro da imagem, para sobrar ao menos 1 px.
  const x = Math.min(sourceWidth - 1, Math.round(selection.left * sourceWidth))
  const y = Math.min(sourceHeight - 1, Math.round(selection.top * sourceHeight))
  return {
    x,
    y,
    width: Math.max(
      1,
      Math.min(sourceWidth - x, Math.round(selection.width * sourceWidth))
    ),
    height: Math.max(
      1,
      Math.min(sourceHeight - y, Math.round(selection.height * sourceHeight))
    ),
  }
}

/**
 * Recorta o canvas de origem e devolve um arquivo PNG pronto para upload.
 */
export function cropCanvasToFile(source, selection, name) {
  const rect = selectionToSourceRect(selection, source.width, source.height)
  const canvas = document.createElement('canvas')
  canvas.width = rect.width
  canvas.height = rect.height
  canvas
    .getContext('2d')
    .drawImage(
      source,
      rect.x,
      rect.y,
      rect.width,
      rect.height,
      0,
      0,
      rect.width,
      rect.height
    )
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Could not encode the screenshot.'))
        return
      }
      resolve(new File([blob], name, { type: 'image/png' }))
    }, 'image/png')
  })
}
